/**
 * The compact form of a case term, and how to read it back.
 *
 * The cases are most of the level content, and most of the cases are unary
 * numerals and cons chains: `oddsum 30` is a thousand characters of `succ` and
 * `zero`, and the level's own label already says `20`. The compact form writes
 * a numeral as its digits and a literal list as `[a b]` — the shape the labels
 * have always used — and the interface expands it before the engine sees the
 * term.
 *
 * This is not compression. The terms are *read*, and the parts of them that
 * are derivable are written once instead of spelled out: a chain of `succ` is
 * a number, and a chain of `cons` ending in `nil` is a list. Nothing is
 * approximated, and a term whose packed form does not read back to exactly what
 * it came from is left alone, so the packed content can never differ in meaning
 * from the content it was packed from.
 *
 * Nothing here is a name the language can produce: a bare run of digits is not
 * an identifier, and `[` and `]` are not part of the term syntax, so the packed
 * form is unambiguous without quoting anything.
 */

type Node = { atom: string } | { group: Node[] } | { list: Node[] };

/** How deep a chain of `succ` may nest before it is worth writing as digits. */
const NUMERAL_THRESHOLD = 4;

/**
 * Reads a term written in the term syntax as the sequence of items it is.
 *
 * A term is items written one after another (`f (g x)` applies `f`), so the
 * reader reads to the end rather than stopping at the first item, and `null`
 * says the text was not a term at all.
 */
function readTerm(text: string): Node[] | null {
  let at = 0;

  function skip(): void {
    while (at < text.length && text[at] === ' ') {
      at += 1;
    }
  }

  function term(): Node | null {
    skip();
    if (text[at] === '(') {
      at += 1;
      const items: Node[] = [];
      for (;;) {
        skip();
        if (at >= text.length) {
          return null;
        }
        if (text[at] === ')') {
          at += 1;
          return { group: items };
        }
        const item = term();
        if (item === null) {
          return null;
        }
        items.push(item);
      }
    }
    const start = at;
    while (at < text.length && text[at] !== ' ' && text[at] !== '(' && text[at] !== ')') {
      at += 1;
    }
    const word = text.slice(start, at);
    return word === '' ? null : { atom: word };
  }

  const nodes: Node[] = [];
  for (;;) {
    skip();
    if (at >= text.length) {
      return nodes;
    }
    const node = term();
    if (node === null) {
      return null;
    }
    nodes.push(node);
  }
}

/** The same term with the derivable parts written once. */
function compact(node: Node): Node {
  if ('atom' in node) {
    return node;
  }
  if ('group' in node) {
    // `succ` applied down to `zero`: a numeral, written as its digits.
    let depth = 0;
    let rest: Node = node;
    while (
      'group' in rest &&
      rest.group.length === 2 &&
      'atom' in rest.group[0]! &&
      rest.group[0].atom === 'succ'
    ) {
      depth += 1;
      rest = rest.group[1]!;
    }
    if (depth >= NUMERAL_THRESHOLD && 'atom' in rest && rest.atom === 'zero') {
      return { atom: String(depth) };
    }
    // `cons` applied down to `nil`: a list, written between brackets.
    const items: Node[] = [];
    let tail: Node = node;
    while (
      'group' in tail &&
      tail.group.length === 3 &&
      'atom' in tail.group[0]! &&
      tail.group[0].atom === 'cons'
    ) {
      items.push(compact(tail.group[1]!));
      tail = tail.group[2]!;
    }
    if (items.length > 0 && 'atom' in tail && tail.atom === 'nil') {
      return { list: items };
    }
    return { group: node.group.map(compact) };
  }
  return { list: node.list.map(compact) };
}

/** The packed form of one term: the term itself when nothing can be read out. */
export function packTerm(source: string): string {
  const nodes = readTerm(source);
  if (nodes === null) {
    return source;
  }
  const packed = nodes.map((node) => writeTerm(compact(node))).join(' ');
  // A form that does not read back to exactly this term is no use, whatever it
  // saves: the whole point is that the packed content means the same thing.
  return expandTerm(packed) === source ? packed : source;
}

function writeTerm(node: Node): string {
  if ('atom' in node) {
    return node.atom;
  }
  if ('group' in node) {
    return `(${node.group.map(writeTerm).join(' ')})`;
  }
  return `[${node.list.map(writeTerm).join(' ')}]`;
}

/** The numeral `n` written with `zero` and `succ`, as the authoring side does. */
function numeralChain(n: number): string {
  return n === 0 ? 'zero' : `(succ ${numeralChain(n - 1)})`;
}

/** Expands a packed term back into the term the engine reads. */
export function expandTerm(packed: string): string {
  // A term with no packed part in it is already the term the engine reads, and
  // is left byte for byte as it was: only digits and brackets are ever read out
  // of, and neither can appear in an identifier.
  if (!/[\d\[\]]/.test(packed)) {
    return packed;
  }
  let at = 0;

  function skip(): void {
    while (at < packed.length && packed[at] === ' ') {
      at += 1;
    }
  }

  function sequence(until: string): string[] {
    const items: string[] = [];
    for (;;) {
      skip();
      if (at >= packed.length || packed[at] === until) {
        return items;
      }
      items.push(term());
    }
  }

  function term(): string {
    skip();
    const opening = packed[at];
    if (opening === '(') {
      at += 1;
      const items = sequence(')');
      at += 1;
      return `(${items.join(' ')})`;
    }
    if (opening === '[') {
      at += 1;
      const items = sequence(']');
      at += 1;
      return items.reduceRight((tail, item) => `(cons ${item} ${tail})`, 'nil');
    }
    const start = at;
    while (at < packed.length && !' ()[]'.includes(packed[at]!)) {
      at += 1;
    }
    const word = packed.slice(start, at);
    return /^\d+$/.test(word) ? numeralChain(Number(word)) : word;
  }

  return sequence('').join(' ');
}

/** One case with its terms written compactly. */
export interface PackableCase {
  lhs: string;
  rhs: string;
  also?: string[];
}

/** Packs a case's terms in place, leaving everything else about it alone. */
export function packCase<T extends PackableCase>(entry: T): T {
  entry.lhs = packTerm(entry.lhs);
  entry.rhs = packTerm(entry.rhs);
  if (entry.also !== undefined) {
    entry.also = entry.also.map(packTerm);
  }
  return entry;
}
